import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { deleteMemberAutogiroStatus } from 'src/shared/services/memberAutogiroStatus';
import { ArrowDropUp } from '@mui/icons-material';
import { Button, ClickAwayListener, Grid, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { MemberPaymentMethodType } from 'src/shared/models/enums/memberPaymentMethodType.enum';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { useGlobals } from 'src/shared/contexts/appContext';
import { AutogiroSettingsModal } from './autogiroSettingsModal';
import { AutogiroStatus, Member, StripeStatus } from '../models/member';

type MemberPaymentMethodProps = {
  member: Member;
  refetchMemberCallback: () => void;
};

type PaymentMethodConfig = {
  type: MemberPaymentMethodType;
  status: StripeStatus | AutogiroStatus;
  label: string;
  onAdd: () => void;
  onDelete: () => void;
  showDeleteModal: boolean;
  modal: React.ReactNode;
};

export const MemberPaymentMethod = (props: MemberPaymentMethodProps) => {
  const { member, refetchMemberCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [activeModal, setActiveModal] = useState<MemberPaymentMethodType | null>(null);
  const [openPaymentMethodMenu, setOpenPaymentMethodMenu] = useState<boolean>(false);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const hasMemberUpdatePermission = getPermission(currentUser, PermissionEnum.MemberUpdate);

  const closeModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpenPaymentMethodMenu(false);
  };

  const handleToggle = () => {
    setOpenPaymentMethodMenu((prevOpen) => !prevOpen);
  };

  const handleCloseDeleteAutogiro = useCallback(
    // eslint-disable-next-line consistent-return
    async (id: number | undefined) => {
      ConsoleHelper.log('handleCloseDeleteAutogiro', id);
      if (!id) return closeModal();

      try {
        await deleteMemberAutogiroStatus(id);
        if (isMounted()) {
          toast.success(<>{t('member:autogiroIsDeleted')}</>);
          refetchMemberCallback();
          closeModal();
        }
      } catch (e) {
        ConsoleHelper.log(e);
      }
    },
    [closeModal, isMounted, refetchMemberCallback, t],
  );

  // const handleCloseDeleteStripe = useCallback(
  //   // eslint-disable-next-line consistent-return
  //   async (id: number | undefined) => {
  //     ConsoleHelper.log('handleCloseDeleteStripe', id);
  //     if (!id) return closeModal();

  //     try {
  //       await deleteMemberStripeStatus(id);
  //       if (isMounted()) {
  //         toast.success(<>{t('member:stripeIsDeleted')}</>);
  //         refetchMemberCallback();
  //         closeModal();
  //       }
  //     } catch (e) {
  //       ConsoleHelper.log(e);
  //     }
  //   },
  //   [closeModal, isMounted, refetchMemberCallback, t],
  // );

  // Betalmetoder
  const paymentMethods = useMemo<PaymentMethodConfig[]>(
    () => [
      {
        type: MemberPaymentMethodType.autogiro,
        status: member.autogiroStatus,
        label: member.autogiroStatus ? t('member:deleteAutogiro') : t('member:addAutogiro'),
        onAdd: () => setActiveModal(MemberPaymentMethodType.autogiro),
        onDelete: () => setActiveModal(MemberPaymentMethodType.autogiro),
        showDeleteModal: activeModal === MemberPaymentMethodType.autogiro,
        modal: member.autogiroStatus ? (
          <DeleteConfirmationDialog
            title={t('member:confirmDeleteAutogiro')}
            value={member.autogiroStatus.autogiroStatus_id.toString()}
            open={activeModal === MemberPaymentMethodType.autogiro}
            onClose={(val) => {
              if (!val || !val.id) {
                closeModal();
                return;
              }
              handleCloseDeleteAutogiro(Number(val.id));
            }}
          />
        ) : (
          <AutogiroSettingsModal
            openAutogiroSettingsModal={activeModal === MemberPaymentMethodType.autogiro}
            setOpenAutogiroSettingsModal={closeModal}
            member={member}
            refetchMemberCallback={refetchMemberCallback}
          />
        ),
      },
      //   {
      //     type: MemberPaymentMethodType.stripe,
      //     status: member.stripeStatus,
      //     label: member.stripeStatus ? t('member:deleteStripe') : t('member:addStripe'),
      //     onAdd: () => setActiveModal(MemberPaymentMethodType.stripe),
      //     onDelete: () => setActiveModal(MemberPaymentMethodType.stripe),
      //     showDeleteModal: activeModal === MemberPaymentMethodType.stripe,
      //     modal: member.stripeStatus ? (
      //       <DeleteConfirmationDialog
      //         title={t('member:confirmDeleteStripe')}
      //         value={member.stripeStatus.stripeStatus_id.toString()}
      //         open={activeModal === MemberPaymentMethodType.stripe}
      //         onClose={(val) => {
      //           if (!val || !val.id) {
      //             closeModal();
      //             return;
      //           }
      //           handleCloseDeleteStripe(Number(val.id));
      //         }}
      //       />
      //     ) : (
      //       <StripeSettingsModal
      //         openStripeSettingsModal={activeModal === MemberPaymentMethodType.stripe}
      //         setOpenStripeSettingsModal={closeModal}
      //         member={member}
      //         refetchMemberCallback={refetchMemberCallback}
      //       />
      //     ),
      //   },
    ],
    [member, t, activeModal, closeModal, refetchMemberCallback, handleCloseDeleteAutogiro],
  );

  return (
    <>
      <Button
        disabled={!hasMemberUpdatePermission}
        sx={{ mt: 2 }}
        ref={anchorRef}
        id='composition-button'
        aria-controls={openPaymentMethodMenu ? 'composition-menu' : undefined}
        aria-expanded={openPaymentMethodMenu ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
        variant='outlined'
        size='small'
        disableElevation
        endIcon={<ArrowDropUp />}
      >
        {t('member:paymentMethod')}
      </Button>
      <Popper
        open={openPaymentMethodMenu}
        anchorEl={anchorRef.current}
        placement='bottom'
        transition
        disablePortal
        nonce={undefined}
        sx={{ zIndex: 999 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList className='section-inset' id='composition-menu' aria-labelledby='composition-button'>
                  {/* Visa endast aktiva metoder - om ingen är aktiv, visa alla alternativ */}
                  {paymentMethods
                    .filter((pm) => pm.status || paymentMethods.every((m) => !m.status))
                    .map((pm) => (
                      <MenuItem key={pm.type} onClick={pm.status ? pm.onDelete : pm.onAdd}>
                        {pm.label}
                      </MenuItem>
                    ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {paymentMethods.map(
        (pm) =>
          pm.showDeleteModal && (
            <Grid item key={pm.type}>
              {pm.modal}
            </Grid>
          ),
      )}
    </>
  );
};
